<template>
  <div class="modal fade show" style="display: block;" tabindex="-1">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fa fa-times-circle text-danger me-2"></i>
            Reject Closure Request
          </h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        
        <div class="modal-body">
          <!-- Request Summary -->
          <div class="alert alert-info">
            <h6 class="alert-heading mb-2">
              <i class="fa fa-info-circle me-1"></i>
              Request Summary
            </h6>
            <div class="row">
              <div class="col-sm-6">
                <strong>Log Number:</strong><br>
                {{ request.subCategoryLogNo }}
              </div>
              <div class="col-sm-6">
                <strong>SubCategory:</strong><br>
                {{ request.subCategoryId?.name || 'N/A' }}
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-sm-6">
                <strong>Requester:</strong><br>
                {{ request.requestedBy?.name || 'N/A' }}
              </div>
              <div class="col-sm-6">
                <strong>Priority:</strong><br>
                <span 
                  class="badge"
                  :class="getPriorityClass(request.priority)"
                >
                  {{ request.priority?.toUpperCase() || 'MEDIUM' }}
                </span>
                <span v-if="request.isUrgent" class="badge bg-warning ms-1">URGENT</span>
              </div>
            </div>
          </div>
          
          <!-- Rejection Warning -->
          <div class="mb-3">
            <div class="alert alert-warning">
              <i class="fa fa-exclamation-triangle me-2"></i>
              <strong>Important:</strong> Rejecting this request will mark it as rejected and notify the requester. The subcategory log will remain open.
            </div>
          </div>
          
          <!-- Rejection Reason -->
          <div class="mb-3">
            <label for="rejectionReason" class="form-label">
              <i class="fa fa-comment me-1"></i>
              Rejection Reason <span class="text-danger">*</span>
            </label>
            <textarea 
              id="rejectionReason"
              v-model="rejectionReason"
              class="form-control"
              :class="{ 'is-invalid': !rejectionReason && showValidation }"
              rows="4"
              placeholder="Please provide a clear reason for rejecting this closure request..."
              required
            ></textarea>
            <div class="invalid-feedback" v-if="!rejectionReason && showValidation">
              Rejection reason is required
            </div>
            <div class="form-text">
              This reason will be sent to the requester and recorded in the audit trail.
            </div>
          </div>
          
          <!-- Quick Rejection Templates -->
          <div class="mb-3">
            <label class="form-label">
              <i class="fa fa-list me-1"></i>
              Common Rejection Reasons
            </label>
            <div class="d-grid gap-2">
              <button 
                v-for="template in rejectionTemplates" 
                :key="template.title"
                type="button"
                class="btn btn-outline-danger btn-sm text-start"
                @click="selectTemplate(template)"
              >
                <strong>{{ template.title }}</strong>
                <br>
                <small class="text-muted">{{ template.description }}</small>
              </button>
            </div>
          </div>
          
          <!-- Additional Comments -->
          <div class="mb-3">
            <label for="additionalComments" class="form-label">
              <i class="fa fa-plus-circle me-1"></i>
              Additional Comments <span class="text-muted">(Optional)</span>
            </label>
            <textarea 
              id="additionalComments"
              v-model="additionalComments"
              class="form-control"
              rows="3"
              placeholder="Any additional information or guidance for the requester..."
            ></textarea>
          </div>
          
          <!-- Confirmation Checkbox -->
          <div class="form-check mb-3">
            <input 
              class="form-check-input" 
              type="checkbox" 
              id="confirmRejection"
              v-model="confirmRejection"
            >
            <label class="form-check-label fw-bold" for="confirmRejection">
              I confirm that I want to reject this closure request
            </label>
          </div>
        </div>
        
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            <i class="fa fa-times me-1"></i>
            Cancel
          </button>
          
          <button 
            type="button" 
            class="btn btn-danger" 
            @click="handleReject"
            :disabled="!canReject || loading"
          >
            <i class="fa fa-spinner fa-spin me-1" v-if="loading"></i>
            <i class="fa fa-times me-1" v-else></i>
            {{ loading ? 'Processing...' : 'Reject Request' }}
          </button>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Modal backdrop -->
  <div class="modal-backdrop fade show"></div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue';
import { PRIORITY_CONFIG } from '@/services/subcategoryClosureService';

// Props
const props = defineProps({
  request: {
    type: Object,
    required: true
  }
});

// Emits
const emit = defineEmits(['close', 'reject']);

// Reactive data
const rejectionReason = ref('');
const additionalComments = ref('');
const confirmRejection = ref(false);
const loading = ref(false);
const showValidation = ref(false);

// Rejection reason templates
const rejectionTemplates = [
  {
    title: 'Incomplete Documentation',
    description: 'Required documents or information are missing',
    reason: 'The closure request cannot be approved due to incomplete documentation. Please ensure all required documents and information are provided before resubmitting.'
  },
  {
    title: 'Task Not Completed',
    description: 'The subcategory task appears to be incomplete',
    reason: 'The subcategory task does not appear to be fully completed. Please review the requirements and complete all outstanding items before requesting closure.'
  },
  {
    title: 'Quality Standards Not Met',
    description: 'Work quality does not meet established standards',
    reason: 'The work completed does not meet our established quality standards. Please review and address the quality issues before resubmitting the closure request.'
  },
  {
    title: 'Missing Approvals',
    description: 'Required approvals from other departments are missing',
    reason: 'This closure request requires additional approvals from other departments that have not been obtained. Please secure all required approvals before resubmitting.'
  },
  {
    title: 'Premature Request',
    description: 'The request was submitted too early in the process',
    reason: 'This closure request appears to have been submitted prematurely. Please allow the subcategory process to complete fully before requesting closure.'
  },
  {
    title: 'Policy Violation',
    description: 'The request violates company policies or procedures',
    reason: 'This closure request cannot be approved as it violates established company policies or procedures. Please review the relevant policies and make necessary corrections.'
  }
];

// Computed properties
const canReject = computed(() => {
  return rejectionReason.value.trim().length > 0 && confirmRejection.value;
});

const finalRejectionReason = computed(() => {
  let reason = rejectionReason.value.trim();
  if (additionalComments.value.trim()) {
    reason += '\n\nAdditional Comments:\n' + additionalComments.value.trim();
  }
  return reason;
});

// Methods
const getPriorityClass = (priority) => {
  return PRIORITY_CONFIG[priority]?.class || PRIORITY_CONFIG.medium.class;
};

const selectTemplate = (template) => {
  rejectionReason.value = template.reason;
};

const handleReject = async () => {
  showValidation.value = true;
  
  if (!canReject.value) {
    return;
  }
  
  loading.value = true;
  try {
    emit('reject', props.request._id, finalRejectionReason.value);
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.modal {
  z-index: 1055;
}

.modal-backdrop {
  z-index: 1050;
}

.alert {
  border-radius: 0.375rem;
}

.alert-info {
  background-color: rgba(13, 202, 240, 0.1);
  border-color: rgba(13, 202, 240, 0.2);
  color: #055160;
}

.alert-warning {
  background-color: rgba(255, 193, 7, 0.1);
  border-color: rgba(255, 193, 7, 0.2);
  color: #664d03;
}

.badge {
  font-size: 0.75rem;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.btn-outline-danger {
  padding: 0.5rem;
  text-align: left;
}

.btn-outline-danger:hover {
  background-color: rgba(220, 53, 69, 0.1);
}

.form-check-input:checked {
  background-color: #3e5fce;
  border-color: #3e5fce;
}

.form-text {
  font-size: 0.875rem;
  color: #6c757d;
}

.invalid-feedback {
  display: block;
}

.fw-bold {
  font-weight: 600;
}

.text-danger {
  color: #dc3545 !important;
}

.text-muted {
  color: #6c757d !important;
}

.text-start {
  text-align: left !important;
}
</style>